// src/components/league-creation/steps/TradeSettings.js
import React from 'react';
import { ArrowLeftRight, Info, Calendar, Trophy, DollarSign } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { parseIntOrPrev, parseFloatOrPrev, parseFloatOrNull } from '../../../services/formUtils';

const TradeSettings = ({ formData, onInputChange, error }) => {
  const handleChange = (field, value) => {
    onInputChange(field, value);
  };

  return (
    <div style={{ marginBottom: dynastyTheme.tokens.spacing['2xl'] }}>
      {/* Step Header */}
      <div className="text-center" style={{ marginBottom: dynastyTheme.tokens.spacing.xl }}>
        <div 
          className={`flex items-center justify-center mx-auto ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.shadow.primary}`}
          style={{ 
            width: '4rem', 
            height: '4rem',
            borderRadius: dynastyTheme.tokens.radius.lg
          }}
        >
          <ArrowLeftRight className={dynastyTheme.classes.text.white} style={{ width: '2rem', height: '2rem' }} />
        </div>
        <h2 className={`${dynastyTheme.components.heading.h1} ${dynastyTheme.classes.text.white}`} 
            style={{ fontSize: dynastyTheme.tokens.fontSize['2xl'], marginTop: dynastyTheme.tokens.spacing.md }}>
          Trade Settings
        </h2>
        <p className={dynastyTheme.classes.text.neutralLight}>
          Configure how trades work in your league
        </p>
      </div>

      {/* Veto System */}
      <div 
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.lg,
          marginBottom: dynastyTheme.tokens.spacing.lg
        }}
      >
        <h3 className={`${dynastyTheme.classes.text.white} text-lg font-semibold mb-4 flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <Info className={dynastyTheme.classes.text.primary} style={{ width: '1.25rem', height: '1.25rem' }} />
          Veto System
        </h3>
        
        <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
          <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
            Veto Method
          </label>
          <select
            value={formData.veto_system || 'none'}
            onChange={(e) => handleChange('veto_system', e.target.value)}
            className={`${dynastyTheme.components.input} w-full`}
          >
            <option value="none">No Veto - Trades Process Immediately</option>
            <option value="commissioner">Commissioner Approval Required</option>
            <option value="league_vote">League Vote (Can Be Vetoed)</option>
          </select>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
            {formData.veto_system === 'none' && 'Trades process as soon as both teams accept'}
            {formData.veto_system === 'commissioner' && 'Commissioner must approve each trade'}
            {formData.veto_system === 'league_vote' && 'League can vote to veto trades during review period'}
          </p>
        </div>

        {formData.veto_system === 'league_vote' && (
          <>
            <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
              <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
                Veto Threshold: {Math.round((formData.veto_threshold ?? 0.5) * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.05"
                value={formData.veto_threshold ?? 0.5}
                onChange={(e) => handleChange('veto_threshold', parseFloatOrPrev(e.target.value, formData.veto_threshold))}
                className="w-full"
                style={{ accentColor: dynastyTheme.tokens.colors.primary }}
              />
              <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
                Percentage of teams needed to veto a trade
              </p>
            </div>

            <div style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
              <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
                Veto Period (hours)
              </label>
              <input
                type="number"
                min="0"
                max="168"
                value={formData.veto_period_hours ?? 48}
                onChange={(e) => handleChange('veto_period_hours', parseIntOrPrev(e.target.value, formData.veto_period_hours))}
                className={`${dynastyTheme.components.input} w-full`}
              />
              <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
                How long the league has to review/veto trades (0-168 hours)
              </p>
            </div>
          </>
        )}
      </div>

      {/* Trade Deadline */}
      <div 
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.lg,
          marginBottom: dynastyTheme.tokens.spacing.lg
        }}
      >
        <h3 className={`${dynastyTheme.classes.text.white} text-lg font-semibold mb-4 flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <Calendar className={dynastyTheme.classes.text.primary} style={{ width: '1.25rem', height: '1.25rem' }} />
          Trade Deadline
        </h3>
        
        <div style={{ marginBottom: dynastyTheme.tokens.spacing.md }}>
          <div className="flex items-center" style={{ gap: dynastyTheme.tokens.spacing.sm }}>
            <input
              type="checkbox"
              id="trade_deadline_enabled"
              checked={formData.trade_deadline_enabled ?? true}
              onChange={(e) => handleChange('trade_deadline_enabled', e.target.checked)}
              style={{ 
                accentColor: dynastyTheme.tokens.colors.primary,
                borderRadius: dynastyTheme.tokens.radius.sm
              }}
            />
            <label htmlFor="trade_deadline_enabled" className={`${dynastyTheme.classes.text.white} font-medium`}>
              Enable Trade Deadline
            </label>
          </div>
        </div>

        {(formData.trade_deadline_enabled ?? true) && (
          <div>
            <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
              Deadline Date (MM-DD)
            </label>
            <input
              type="text"
              placeholder="08-31"
              value={formData.trade_deadline_date || '08-31'}
              onChange={(e) => handleChange('trade_deadline_date', e.target.value)}
              className={`${dynastyTheme.components.input} w-full`}
            />
            <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
              No trades allowed after this date (format: MM-DD)
            </p>
          </div>
        )}
      </div>

      {/* Draft Picks Trading */}
      <div 
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.lg,
          marginBottom: dynastyTheme.tokens.spacing.lg
        }}
      >
        <h3 className={`${dynastyTheme.classes.text.white} text-lg font-semibold mb-4 flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <Trophy className={dynastyTheme.classes.text.primary} style={{ width: '1.25rem', height: '1.25rem' }} />
          Draft Picks
        </h3>
        
        <div>
          <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
            Future Picks Tradeable
          </label>
          <select
            value={formData.future_picks_tradeable_years ?? 1}
            onChange={(e) => handleChange('future_picks_tradeable_years', parseIntOrPrev(e.target.value, formData.future_picks_tradeable_years))}
            className={`${dynastyTheme.components.input} w-full`}
          >
            <option value="0">Current Year Only</option>
            <option value="1">1 Year Ahead</option>
            <option value="2">2 Years Ahead</option>
            <option value="3">3 Years Ahead</option>
            <option value="4">4 Years Ahead</option>
            <option value="5">5 Years Ahead</option>
          </select>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
            How many years in advance can draft picks be traded?
          </p>
        </div>
      </div>

      {/* FA Cash Trading */}
      <div 
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.lg,
          marginBottom: dynastyTheme.tokens.spacing.lg
        }}
      >
        <h3 className={`${dynastyTheme.classes.text.white} text-lg font-semibold mb-4 flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <DollarSign className={dynastyTheme.classes.text.primary} style={{ width: '1.25rem', height: '1.25rem' }} />
          FAAB / Cash Trading
        </h3>
        
        <div style={{ marginBottom: dynastyTheme.tokens.spacing.md }}>
          <div className="flex items-center" style={{ gap: dynastyTheme.tokens.spacing.sm }}>
            <input
              type="checkbox"
              id="fa_cash_tradeable"
              checked={formData.fa_cash_tradeable ?? true}
              onChange={(e) => handleChange('fa_cash_tradeable', e.target.checked)}
              style={{ 
                accentColor: dynastyTheme.tokens.colors.primary,
                borderRadius: dynastyTheme.tokens.radius.sm
              }}
            />
            <label htmlFor="fa_cash_tradeable" className={`${dynastyTheme.classes.text.white} font-medium`}>
              Allow FAAB/Cash in Trades
            </label>
          </div>
        </div>

        {(formData.fa_cash_tradeable ?? true) && (
          <div>
            <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
              Maximum Cash Per Trade (optional)
            </label>
            <input
              type="number"
              min="0"
              placeholder="Unlimited"
              value={formData.max_fa_cash_per_trade || ''}
              onChange={(e) => handleChange('max_fa_cash_per_trade', parseFloatOrNull(e.target.value))}
              className={`${dynastyTheme.components.input} w-full`}
            />
            <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
              Leave empty for no limit. Teams can trade their available FAAB.
            </p>
          </div>
        )}
      </div>

      {/* Roster Size Limits */}
      <div 
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.lg,
          marginBottom: dynastyTheme.tokens.spacing.lg
        }}
      >
        <h3 className={`${dynastyTheme.classes.text.white} text-lg font-semibold mb-4`}>
          Roster Size Limits
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: dynastyTheme.tokens.spacing.lg }}>
          <div>
            <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
              Minimum Roster Size
            </label>
            <input
              type="number"
              min="15"
              max="50"
              value={formData.min_roster_size ?? 25}
              onChange={(e) => handleChange('min_roster_size', parseIntOrPrev(e.target.value, formData.min_roster_size))}
              className={`${dynastyTheme.components.input} w-full`}
            />
            <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
              Trades must leave at least this many players
            </p>
          </div>

          <div>
            <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white}`}>
              Maximum Roster Size
            </label>
            <input
              type="number"
              min="20"
              max="60"
              value={formData.max_roster_size ?? 40}
              onChange={(e) => handleChange('max_roster_size', parseIntOrPrev(e.target.value, formData.max_roster_size))}
              className={`${dynastyTheme.components.input} w-full`}
            />
            <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
              Trades cannot exceed this roster size
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div 
        className={`${dynastyTheme.classes.bg.primary}/10 border ${dynastyTheme.classes.border.primary}/30`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.md
        }}
      >
        <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-2`} style={{ fontSize: dynastyTheme.tokens.fontSize.sm }}>
          Summary
        </h4>
        <ul className={`${dynastyTheme.classes.text.neutralLight} text-xs space-y-1`}>
          <li>• Veto: {formData.veto_system === 'none' ? 'None' : formData.veto_system === 'commissioner' ? 'Commissioner' : 'League Vote'}</li>
          <li>• Deadline: {(formData.trade_deadline_enabled ?? true) ? formData.trade_deadline_date || '08-31' : 'None'}</li>
          <li>• Future picks: {formData.future_picks_tradeable_years ?? 1} years ahead</li>
          <li>• Cash trading: {(formData.fa_cash_tradeable ?? true) ? 'Enabled' : 'Disabled'}</li>
          <li>• Roster size: {formData.min_roster_size ?? 25} - {formData.max_roster_size ?? 40} players</li>
        </ul>
      </div>
    </div>
  );
};

export default TradeSettings;